<template>
  <div class="hashtags-container">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 3 7 21M17 3l-2 18M4 9h17M3 15h17"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Поиск вакансии
        </span>

        <h3 class="step-heading-title">
          Навыки и хэштеги
        </h3>
      </div>

      <span class="optional-badge">
        Необязательно
      </span>
    </div>

    <!-- Описание -->
    <p class="step-description">
      Добавьте навыки и ключевые слова, чтобы вакансию могли найти
      пользователи с подходящими интересами и опытом.
    </p>

    <!-- Выбранные -->
    <div class="selected-heading">
      <span class="selected-title">
        Выбранные хэштеги
      </span>

      <span
        class="selected-counter"
        :class="{ active: selectedHashtags.length > 0 }"
      >
        {{ selectedHashtags.length }}
      </span>
    </div>

    <TransitionGroup
      v-if="selectedHashtags.length > 0"
      name="selected-tag"
      tag="div"
      class="selected-hashtags"
    >
      <div
        v-for="hashtag in selectedHashtags"
        :key="hashtagKey(hashtag)"
        class="selected-hashtag"
      >
        <span class="hashtag-symbol">#</span>

        <span class="selected-hashtag-name">
          {{ hashtag.name }}
        </span>

        <button
          type="button"
          class="remove-button"
          :aria-label="`Удалить хэштег ${hashtag.name}`"
          @click="removeHashtag(hashtag)"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </TransitionGroup>

    <div
      v-else
      class="selected-empty"
    >
      Пока ничего не выбрано
    </div>

    <!-- Поиск -->
    <div class="search-section">
      <label
        class="search-label"
        for="hashtag-search"
      >
        Найти хэштег
      </label>

      <div
        class="search-bar"
        :class="{
          loading: isLoading,
          filled: searchQuery.trim().length > 0
        }"
      >
        <div class="search-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10.5"
              cy="10.5"
              r="6.5"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="m15.5 15.5 4 4"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <span class="search-prefix">#</span>

        <input
          id="hashtag-search"
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Например: swift, дизайн, маркетинг"
          autocomplete="off"
          spellcheck="false"
        />

        <Transition name="search-action" mode="out-in">
          <div
            v-if="isLoading"
            key="loader"
            class="search-loader"
          ></div>

          <button
            v-else-if="searchQuery"
            key="clear"
            type="button"
            class="clear-search-button"
            aria-label="Очистить поиск"
            @click="clearSearch"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </Transition>
      </div>

      <span class="search-hint">
        Поиск начнётся автоматически
      </span>
    </div>

    <!-- Результаты -->
    <Transition name="results" mode="out-in">
      <div
        v-if="
          normalizedSearchQuery &&
          !isLoading &&
          hashtags.length > 0
        "
        key="results"
        class="results-section"
      >
        <div class="results-heading">
          <span>Результаты поиска</span>

          <span>
            {{ hashtags.length }}
          </span>
        </div>

        <div class="hashtags-list">
          <button
            v-for="hashtag in hashtags"
            :key="hashtagKey(hashtag)"
            type="button"
            class="hashtag"
            :class="{
              selected: isSelected(hashtag)
            }"
            @click="toggleHashtag(hashtag)"
          >
            <span class="result-hashtag-symbol">
              #
            </span>

            <span class="result-name">
              {{ hashtag.name }}
            </span>

            <span class="result-action">
              <svg
                v-if="isSelected(hashtag)"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m5.5 10.2 2.8 2.8 6.2-6.2"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <svg
                v-else
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M10 5v10M5 10h10"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <!-- Пустой поиск -->
      <div
        v-else-if="
          normalizedSearchQuery &&
          !isLoading &&
          searchCompleted &&
          hashtags.length === 0
        "
        key="empty"
        class="empty-results"
      >
        <div class="empty-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10.5"
              cy="10.5"
              r="6.5"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="m15.5 15.5 4 4M8.5 8.5l4 4M12.5 8.5l-4 4"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <strong>Хэштеги не найдены</strong>

        <span>
          Попробуйте изменить запрос
        </span>
      </div>
    </Transition>

    <!-- Подсказка -->
    <div class="hashtags-hint">
      <div class="hint-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <path
            d="M12 11v5M12 7.5v.2"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <p>
        Лучше выбрать несколько точных навыков, чем большое количество
        общих слов. Например: Swift, UIKit, Figma или Performance Marketing.
      </p>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";

import { debounce } from "lodash";
import HashtagService from "../../api/HashtagService.js";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

const searchQuery = ref("");
const hashtags = ref([]);
const selectedHashtags = ref(
  restoreSavedHashtags()
);

const isLoading = ref(false);
const searchCompleted = ref(false);

let searchSequence = 0;

const normalizedSearchQuery = computed(() =>
  searchQuery.value
    .trim()
    .replace(/^#+/, "")
);

function normalizeHashtagName(value) {
  return String(value || "")
    .trim()
    .replace(/^#+/, "")
    .toLocaleLowerCase("ru-RU");
}

function hashtagKey(hashtag) {
  return (
    hashtag.id ||
    `saved-${normalizeHashtagName(hashtag.name)}`
  );
}

function restoreSavedHashtags() {
  try {
    const raw =
      localStorage.getItem(
        "vacancy_hashtags"
      );

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item, index) => {
        if (typeof item === "string") {
          const name =
            item.trim().replace(/^#+/, "");

          return name
            ? {
                id: `saved-${index}-${name}`,
                name
              }
            : null;
        }

        if (
          item &&
          typeof item === "object" &&
          item.name
        ) {
          return {
            id:
              item.id ||
              `saved-${index}-${item.name}`,
            name: String(item.name)
              .trim()
              .replace(/^#+/, "")
          };
        }

        return null;
      })
      .filter(Boolean);
  } catch (error) {
    console.warn(
      "⚠️ Не удалось восстановить хэштеги:",
      error
    );

    return [];
  }
}

function isSelected(hashtag) {
  const normalizedName =
    normalizeHashtagName(hashtag.name);

  return selectedHashtags.value.some(
    item =>
      normalizeHashtagName(item.name) ===
      normalizedName
  );
}

function toggleHashtag(hashtag) {
  if (isSelected(hashtag)) {
    removeHashtag(hashtag);
    return;
  }

  selectedHashtags.value.push({
    id:
      hashtag.id ||
      `selected-${Date.now()}`,
    name: String(hashtag.name)
      .trim()
      .replace(/^#+/, "")
  });

  clearSearch();
}

function removeHashtag(hashtag) {
  const normalizedName =
    normalizeHashtagName(hashtag.name);

  selectedHashtags.value =
    selectedHashtags.value.filter(
      item =>
        normalizeHashtagName(item.name) !==
        normalizedName
    );
}

function clearSearch() {
  searchQuery.value = "";
  hashtags.value = [];
  searchCompleted.value = false;
  isLoading.value = false;

  searchHashtags.cancel();
  searchSequence += 1;
}

async function performSearch(query) {
  const normalizedQuery =
    query.trim().replace(/^#+/, "");

  if (!normalizedQuery) {
    hashtags.value = [];
    searchCompleted.value = false;
    isLoading.value = false;
    return;
  }

  const currentSequence =
    ++searchSequence;

  isLoading.value = true;
  searchCompleted.value = false;

  try {
    const result =
      await HashtagService.searchHashtags(
        normalizedQuery
      );

    /*
      Игнорируем устаревший ответ, если пользователь
      уже успел ввести другой запрос.
    */
    if (currentSequence !== searchSequence) {
      return;
    }

    hashtags.value =
      Array.isArray(result)
        ? result.filter(item => item?.name)
        : [];

    searchCompleted.value = true;
  } catch (error) {
    if (currentSequence !== searchSequence) {
      return;
    }

    console.error(
      "❌ Ошибка поиска хэштегов:",
      error
    );

    hashtags.value = [];
    searchCompleted.value = true;
  } finally {
    if (currentSequence === searchSequence) {
      isLoading.value = false;
    }
  }
}

const searchHashtags = debounce(
  performSearch,
  450
);

function saveHashtags() {
  const dataToSave =
    selectedHashtags.value.map(
      hashtag => hashtag.name
    );

  localStorage.setItem(
    "vacancy_hashtags",
    JSON.stringify(dataToSave)
  );

  console.log(
    "💾 Хэштеги сохранены:",
    dataToSave
  );

  emit("complete-step");
}

watch(
  searchQuery,
  value => {
    const normalized =
      value.trim().replace(/^#+/, "");

    searchHashtags.cancel();
    searchSequence += 1;

    if (!normalized) {
      hashtags.value = [];
      searchCompleted.value = false;
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    searchCompleted.value = false;

    searchHashtags(normalized);
  }
);

watch(
  selectedHashtags,
  value => {
    /*
      Сохраняем сразу, чтобы выбранные хэштеги
      не потерялись при возврате назад.
    */
    const names = value.map(
      hashtag => hashtag.name
    );

    localStorage.setItem(
      "vacancy_hashtags",
      JSON.stringify(names)
    );

    emit("valid");
  },
  {
    deep: true,
    immediate: true
  }
);

onMounted(() => {
  document.addEventListener(
    "save-hashtags",
    saveHashtags
  );

  /*
    Шаг необязательный.
  */
  emit("valid");
});

onUnmounted(() => {
  document.removeEventListener(
    "save-hashtags",
    saveHashtags
  );

  searchHashtags.cancel();
  searchSequence += 1;
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.hashtags-container {
  width: 100%;
  max-width: 760px;

  display: flex;
  align-items: stretch;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
}

.step-description {
  max-width: 680px;
  margin: 0 0 20px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Selected */

.selected-heading {
  display: flex;
  align-items: center;
  gap: 7px;

  margin-bottom: 9px;
}

.selected-title {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.selected-counter {
  min-width: 21px;
  height: 21px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0 5px;

  color: #8e96a2;
  background: #eef0f3;

  border-radius: 7px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 8px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

.selected-counter.active {
  color: #ffffff;
  background: #3d86ec;
}

.selected-hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  margin-bottom: 17px;
}

.selected-hashtag {
  min-height: 35px;

  display: inline-flex;
  align-items: center;
  gap: 5px;

  padding: 5px 6px 5px 10px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3594f5,
      #3a72df
    );

  border-radius: 11px;

  box-shadow:
    0 5px 12px rgba(46, 112, 218, 0.15);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
}

.hashtag-symbol {
  color: rgba(255, 255, 255, 0.7);
}

.selected-hashtag-name {
  max-width: 190px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.remove-button {
  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  cursor: pointer;

  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.13);

  border: none;
  border-radius: 8px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.remove-button:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.22);

  transform: scale(1.06);
}

.remove-button svg {
  width: 14px;
  height: 14px;
}

.selected-empty {
  margin-bottom: 17px;
  padding: 11px 12px;

  color: #989faa;
  background: #f8f9fb;

  border: 1px dashed #dfe3e9;
  border-radius: 11px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

/* Search */

.search-section {
  width: 100%;
}

.search-label {
  display: block;

  margin-bottom: 7px;

  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.search-bar {
  width: 100%;
  min-height: 51px;

  display: flex;
  align-items: center;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.search-bar:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.search-bar:focus-within {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 22px rgba(31, 96, 205, 0.07);

  transform: translateY(-1px);
}

.search-icon {
  width: 19px;
  height: 19px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 15px;

  color: #929ba8;
}

.search-icon svg {
  width: 19px;
  height: 19px;
}

.search-bar:focus-within .search-icon {
  color: #4388f5;
}

.search-prefix {
  margin-left: 10px;

  color: #4388f5;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
}

.search-input {
  min-width: 0;
  width: 100%;
  height: 49px;

  padding: 0 9px;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

.search-input::placeholder {
  color: #a4abb6;
}

.clear-search-button {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 11px;
  padding: 0;

  cursor: pointer;

  color: #89919d;
  background: #e9ecf0;

  border: none;
  border-radius: 8px;
}

.clear-search-button svg {
  width: 15px;
  height: 15px;
}

.search-loader {
  width: 21px;
  height: 21px;

  flex-shrink: 0;

  margin-right: 14px;

  border: 2px solid rgba(67, 136, 245, 0.16);
  border-top-color: #4388f5;
  border-radius: 50%;

  animation: spin 0.7s linear infinite;
}

.search-hint {
  display: block;

  margin: 6px 0 0 4px;

  color: #989faa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* Results */

.results-section {
  margin-top: 18px;
}

.results-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 9px;

  color: #7c8593;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.hashtags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.hashtag {
  min-height: 36px;

  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 6px 7px 6px 11px;

  cursor: pointer;

  color: #596476;
  background: #f6f7f9;

  border: 1px solid #e3e6ea;
  border-radius: 11px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.hashtag:hover {
  color: #3479d8;
  background: #ffffff;
  border-color: rgba(52, 121, 216, 0.25);

  box-shadow:
    0 6px 14px rgba(38, 93, 170, 0.07);

  transform: translateY(-1px);
}

.hashtag.selected {
  color: #ffffff;
  background: #3985e9;
  border-color: #3985e9;
}

.result-hashtag-symbol {
  color: #4388f5;
}

.hashtag.selected .result-hashtag-symbol {
  color: rgba(255, 255, 255, 0.7);
}

.result-action {
  width: 22px;
  height: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.08);

  border-radius: 7px;
}

.hashtag.selected .result-action {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.15);
}

.result-action svg {
  width: 14px;
  height: 14px;
}

/* Empty */

.empty-results {
  min-height: 180px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  margin-top: 18px;
  padding: 20px;

  text-align: center;
  background: #f8f9fb;

  border: 1px dashed #d8dde5;
  border-radius: 15px;
}

.empty-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 10px;

  color: #8ca0bc;
  background: #eef2f7;

  border-radius: 13px;
}

.empty-icon svg {
  width: 23px;
  height: 23px;
}

.empty-results strong {
  margin-bottom: 4px;

  color: #414954;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.empty-results span {
  color: #929aa7;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

/* Hint */

.hashtags-hint {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  margin-top: 20px;
  padding: 11px 12px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 12px;
}

.hint-icon {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  color: #4388f5;
}

.hint-icon svg {
  width: 17px;
  height: 17px;
}

.hashtags-hint p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.55;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.selected-tag-enter-active,
.selected-tag-leave-active,
.search-action-enter-active,
.search-action-leave-active,
.results-enter-active,
.results-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.selected-tag-enter-from,
.selected-tag-leave-to {
  opacity: 0;
  transform: scale(0.75);
}

.selected-tag-leave-active {
  position: absolute;
}

.search-action-enter-from,
.search-action-leave-to {
  opacity: 0;
  transform: scale(0.65);
}

.results-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.results-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Dark theme */

html[data-theme="dark"] .hashtags-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .optional-badge {
  color: #a3a6b6;
  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.065);
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .selected-title,
html[data-theme="dark"] .search-label {
  color: #d8dae0;
}

html[data-theme="dark"] .selected-counter {
  color: #969da8;
  background: #303136;
}

html[data-theme="dark"] .selected-counter.active {
  color: #ffffff;
  background: #3c7dd6;
}

html[data-theme="dark"] .selected-empty {
  color: #777e89;
  background: #27272b;
  border-color: #3d3e44;
}

html[data-theme="dark"] .search-bar {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .search-bar:hover {
  background: #2b2b30;
  border-color: #484850;
}

html[data-theme="dark"] .search-bar:focus-within {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .search-input {
  color: #f1f2f4;
}

html[data-theme="dark"] .search-input::placeholder {
  color: #6f747e;
}

html[data-theme="dark"] .clear-search-button {
  color: #9ba1ab;
  background: #3c3d43;
}

html[data-theme="dark"] .hashtag {
  color: #c9cdd4;
  background: #29292d;
  border-color: #3b3b41;
}

html[data-theme="dark"] .hashtag:hover {
  color: #a8c8f4;
  background: #2e2e33;
  border-color: rgba(81, 148, 241, 0.28);

  box-shadow:
    0 7px 16px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .hashtag.selected {
  color: #ffffff;
  background: #397bd4;
  border-color: #397bd4;
}

html[data-theme="dark"] .empty-results {
  background: #27272b;
  border-color: #3e4047;
}

html[data-theme="dark"] .empty-icon {
  color: #879bb6;
  background: #303239;
}

html[data-theme="dark"] .empty-results strong {
  color: #dcdee3;
}

html[data-theme="dark"] .empty-results span {
  color: #7b828e;
}

html[data-theme="dark"] .hashtags-hint {
  color: #9ba2ae;
  background: rgba(63, 137, 241, 0.07);
  border-color: rgba(74, 145, 246, 0.12);
}

/* Mobile */

@media (max-width: 600px) {
  .hashtags-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;
    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .optional-badge {
    display: none;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .selected-hashtag-name {
    max-width: 150px;
  }

  .search-input {
    font-size: 16px;
  }

  .hashtags-list {
    max-height: calc(100dvh - 430px);

    overflow-y: auto;
    overflow-x: hidden;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hashtags-container,
  .search-loader {
    animation: none;
  }

  .selected-tag-enter-active,
  .selected-tag-leave-active,
  .search-action-enter-active,
  .search-action-leave-active,
  .results-enter-active,
  .results-leave-active,
  .search-bar,
  .remove-button,
  .hashtag {
    transition: none;
  }
}
</style>